import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { api } from "@/api";
import { useMarkNotificationReadBySource } from "@/api/use-notifications";
import { ErrorState } from "@/components/common/states";
import { InvoiceView } from "@/components/orders/invoice-view";
import { OrderReceipt } from "@/components/orders/order-receipt";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@osmix/ui/skeleton";
import { useProfile } from "@/store";
import { ecPurchaseFromOrder, METRIKA_GOALS, reachGoalOnce } from "@/lib/metrika-ecommerce";

// Order confirmation. The order id arrives via ?order= (set by checkout on a
// successful payment) and is re-fetched here so a reload still shows the
// receipt — the cart is already cleared by this point.
export function CheckoutSuccessPage() {
  const $ = _c(69);
  const {
    t
  } = useTranslation();
  const [params] = useSearchParams();
  let t0;
  if ($[0] !== params) {
    t0 = params.get("order") ?? "";
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const id = t0;
  const addOrder = useProfile(_temp);
  const token = useProfile(_temp2);
  const user = useProfile(_temp3);
  const {
    mutate: markReadBySource
  } = useMarkNotificationReadBySource();
  const markedSource = useRef(null);
  let t1;
  if ($[2] !== id) {
    t1 = {
      queryKey: ["orders", "get", id],
      queryFn: () => api.orders.get(id)
    };
    $[2] = id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const order = useQuery(t1);
  let t2;
  if ($[4] !== id) {
    t2 = {
      queryKey: ["orders", "shipments", id],
      queryFn: () => api.orders.shipments(id)
    };
    $[4] = id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const shipments = useQuery(t2);
  const isInvoiceOrder = order.data?.paymentMethod === "invoice";
  let t3;
  let t4;
  if ($[6] !== id) {
    t3 = ["buyer", "invoice", id];
    t4 = () => api.buyer.getOrderInvoice(id);
    $[6] = id;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  const t5 = Boolean(id && isInvoiceOrder && token && user);
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      retry: false
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const invoice = useQuery(t6);
  let t7;
  if ($[13] !== addOrder || $[14] !== order.data) {
    t7 = () => {
      if (order.data?.id) {
        addOrder(order.data.id);
      }
    };
    $[13] = addOrder;
    $[14] = order.data;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = order.data?.id;
  let t9;
  if ($[16] !== addOrder || $[17] !== t8) {
    t9 = [addOrder, t8];
    $[16] = addOrder;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  useEffect(t7, t9);
  let t10;
  let t11;
  if ($[19] !== order.data) {
    t10 = () => {
      const o = order.data;
      if (!o?.id) {
        return;
      }
      ecPurchaseFromOrder(o);
      if (o.payment?.status === "approved") {
        reachGoalOnce(METRIKA_GOALS.payment, o.id);
      }
    };
    t11 = [order.data];
    $[19] = order.data;
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[22] !== markReadBySource || $[23] !== order.data || $[24] !== token || $[25] !== user) {
    t12 = () => {
      if (!token || !user || !order.data?.id || markedSource.current === order.data.id) {
        return;
      }
      markedSource.current = order.data.id;
      markReadBySource({
        kind: "purchase_receipt",
        sourceId: order.data.id
      });
    };
    t13 = [markReadBySource, order.data, token, user];
    $[22] = markReadBySource;
    $[23] = order.data;
    $[24] = token;
    $[25] = user;
    $[26] = t12;
    $[27] = t13;
  } else {
    t12 = $[26];
    t13 = $[27];
  }
  useEffect(t12, t13);
  if (order.isPending) {
    let t14;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <div className="mx-auto flex max-w-xl flex-col items-center gap-4 py-12"><Skeleton className="size-16 rounded-full" /><Skeleton className="h-8 w-64" /><Skeleton className="h-32 w-full rounded-2xl" /></div>;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    return t14;
  }
  if (order.error || !order.data) {
    let t14;
    if ($[29] !== order) {
      t14 = <ErrorState onRetry={() => order.refetch()} />;
      $[29] = order;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    return t14;
  }
  const o_0 = order.data;
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="flex size-16 items-center justify-center rounded-full bg-status-published/10 text-status-published"><CheckCircle2 className="size-9" aria-hidden="true" /></span>;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("success.title");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = <h1 className="type-h1-bold">{t15}</h1>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t) {
    t17 = t("success.subtitle");
    $[36] = t;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t17) {
    t18 = <p className="type-body1 text-osmix-text-2">{t17}</p>;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t16 || $[41] !== t18) {
    t19 = <div className="flex flex-col gap-2">{t16}{t18}</div>;
    $[40] = t16;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== shipments.data) {
    t20 = shipments.data ?? [];
    $[43] = shipments.data;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== o_0 || $[46] !== t20) {
    t21 = <OrderReceipt order={o_0} shipments={t20} />;
    $[45] = o_0;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== invoice || $[49] !== isInvoiceOrder || $[50] !== t) {
    t22 = isInvoiceOrder && <div className="w-full rounded-2xl bg-card p-5 text-left shadow-block"><p className="mb-3 type-body2 text-osmix-text-2">{t("checkout.invoiceOrderNote")}</p>{invoice.isPending ? <Skeleton className="h-64 rounded-2xl" /> : invoice.isError ? <p className="type-body2 text-destructive">{invoice.error instanceof Error ? invoice.error.message : t("common.error")}</p> : invoice.data ? <InvoiceView invoice={invoice.data} /> : null}</div>;
    $[48] = invoice;
    $[49] = isInvoiceOrder;
    $[50] = t;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== o_0 || $[53] !== t || $[54] !== token || $[55] !== user) {
    t23 = token && user && <Button variant="gray" size="md" asChild={true}><Link to={`/profile/orders/${encodeURIComponent(o_0.id)}`}>{t("success.viewInOrders")}</Link></Button>;
    $[52] = o_0;
    $[53] = t;
    $[54] = token;
    $[55] = user;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t) {
    t24 = t("success.continueShopping");
    $[57] = t;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t24) {
    t25 = <Button variant="accent" size="md" asChild={true}><Link to="/catalog">{t24}</Link></Button>;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t23 || $[62] !== t25) {
    t26 = <div className="flex flex-wrap justify-center gap-3">{t23}{t25}</div>;
    $[61] = t23;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t19 || $[65] !== t21 || $[66] !== t22 || $[67] !== t26) {
    t27 = <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 py-8 text-center">{t14}{t19}{t21}{t22}{t26}</div>;
    $[64] = t19;
    $[65] = t21;
    $[66] = t22;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  return t27;
}
function _temp3(s_1) {
  return s_1.user;
}
function _temp2(s_0) {
  return s_0.token;
}
function _temp(s) {
  return s.addOrder;
}